<script setup lang="ts">
const queryClient = useQueryClient();
await queryClient.ensureQueryData(contactPageQueryOptions());
const { page, entrance, contactForm, about, location } = useContactPage();
useSeo({ page });

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: 'Forside', item: '/' },
      { name: 'Kontakt' },
    ],
  }),
]);
</script>

<template>
  <div>
    <!-- Hero: heading + contact info + form -->
    <div class="mx-auto max-w-6xl px-6 pt-16 pb-12">
      <h1 class="text-3xl md:text-4xl font-bold">
        {{ entrance?.value?.heading }}
      </h1>

      <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
      <div
        class="mt-3 max-w-2xl cms-prose"
        v-html="entrance?.value?.content"
      />

      <div class="mt-10 grid grid-cols-1 items-start gap-10 lg:grid-cols-5">
        <!-- Left: Contact cards (stacked on desktop to fill height) -->
        <div class="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
          <NuxtLink
            v-for="(contact, i) in entrance?.value?.contacts"
            :key="i"
            :href="contact.href"
            target="_blank"
            :aria-label="contact.heading || contact.content"
            class="group flex items-center gap-3 rounded-xl bg-white px-4 py-3 border border-[var(--color-warm-border)]/50 shadow-[0_1px_8px_rgba(45,41,38,0.04)] hover:shadow-[0_2px_16px_rgba(45,41,38,0.08)] hover:border-primary-300 transition-all duration-200"
          >
            <span class="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-warm-gray)] text-[var(--color-charcoal)]/60 group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
              <UIcon
                :name="mapIcon(contact.icon.content)"
                class="size-4"
              />
            </span>
            <div class="min-w-0">
              <p class="text-xs text-[var(--color-charcoal)]/50 leading-tight">
                {{ contact.heading }}
              </p>
              <p class="mt-0.5 text-sm font-medium text-[var(--color-charcoal)] truncate">
                {{ contact.content }}
              </p>
            </div>
          </NuxtLink>
        </div>

        <!-- Right: Contact form (3 cols on lg) -->
        <div class="lg:col-span-3 bg-white rounded-2xl p-6 md:p-8 shadow-[0_2px_24px_rgba(45,41,38,0.06)] border border-[var(--color-warm-border)]/50">
          <h2 class="mb-1 text-xl font-semibold">
            {{ contactForm?.value?.heading }}
          </h2>
          <p class="text-sm text-[var(--color-charcoal)]/50 mb-6">
            Skriv til os, og vi svarer hurtigst muligt.
          </p>

          <ContactForm />
        </div>
      </div>
    </div>

    <!-- About + Location section -->
    <div
      v-if="about || location"
      class="bg-[var(--color-warm-gray)]/50 py-16"
    >
      <div
        v-if="about"
        class="mx-auto max-w-3xl px-6 text-center"
      >
        <h2 class="text-2xl font-medium">
          {{ about?.value?.heading }}
        </h2>

        <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
        <blockquote
          class="mx-auto mt-3 cms-prose"
          v-html="about?.value?.content"
        />
      </div>

      <!-- Location section -->
      <div
        v-if="location"
        :class="about ? 'mt-14' : ''"
      >
        <div class="mx-auto max-w-6xl px-6">
          <h2 class="text-2xl font-medium">
            {{ location?.value?.heading }}
          </h2>
          <NuxtLink
            :href="location?.value?.address?.google_maps_link"
            target="_blank"
            class="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-charcoal)]/70 underline underline-offset-2 hover:text-primary-600 transition-colors"
          >
            <UIcon
              name="i-lucide-map-pin"
              class="size-4 shrink-0"
            />
            {{ location?.value?.address?.content }}
          </NuxtLink>

          <div
            v-if="location?.value?.address?.google_maps_embed"
            class="mt-5 h-[320px] overflow-hidden rounded-xl border border-[var(--color-warm-border)]/50 shadow-[0_1px_8px_rgba(45,41,38,0.04)]"
          >
            <LazyVisibleMap
              :src="location.value.address.google_maps_embed"
              class="h-full w-full"
              hydrate-on-visible
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
